import React from 'react'
import Box from '@mui/material/Box'
import Stack from '@mui/material/Stack'
import Typography from '@mui/material/Typography'
import { alpha, useTheme } from '@mui/material/styles'
import { useSmartTranslation } from '../../../../generic/hooks/useSmartTranslation'
import { useDateFormatter } from '../../../../generic/hooks/useDateFormatter'
import { raisedColor } from '../detailSurfaces'
import { PARTIALLY_FINISHED } from '../../statusProgressLabel'
import { EMPTY_DASH } from '../detailShared'

// RN60: the four milestones ALWAYS render, in this fixed order — never derived from whatever
// order the DTO happens to list them in.
//
// ⚠️⚠️ S417 — «LANZADA» SE FUE Y «PLANIFICADA» OCUPA SU SITIO, y el cambio no es cosmético. Sun
// fusionó LAUNCHED en STARTED, y CUÁL DE LOS DOS SOBREVIVIÓ ES TODO EL ASUNTO: LAUNCHED solo podía
// leerse de `fabrication_order_transition`, una tabla que está vacía y que no se rellena nunca
// (D16), así que era ESTRUCTURALMENTE INCAPAZ de llevar fecha. STARTED lee el histórico de piezas
// de la planta, así que sí la lleva.
//
// 📌 Y ésa es exactamente la avería que esto arregla. Mientras LAUNCHED siguió en el esqueleto,
// RN60 lo pintaba igual —el esqueleto sale entero venga o no en el DTO— y decía «sin registro»
// PARA SIEMPRE Y EN TODAS LAS ÓRDENES: ya no existe hito capaz de alcanzarlo. Un hueco permanente
// que no informa de nada es justo lo que RN8 prohíbe, solo que aquí lo producía la lista.
//
// ⚠️ PLANNED iba detrás por no estar aquí: `milestoneKindsOf` concatena al final lo que llega y no
// reconoce. Que se viera —en crudo y el último— en vez de desaparecer es para lo que se escribió
// ese respaldo; el respaldo se queda, pero deja de hacer este trabajo.
const MILESTONE_ORDER = ['CREATED', 'PLANNED', 'STARTED', 'CLOSED']

// ⚠️ EL ORDEN FIJO NO ES LA LISTA, y la distinción nace al convertir los hitos en una línea de
// tiempo. RN60 exige que los cuatro salgan siempre —un tramo que falta no se lee como «ese hito no
// está registrado», se lee como que el bloque se ha roto—, pero recorrer SOLO esos cuatro dejaría
// fuera un quinto hito el día que Sun lo sirva, sin error, sin hueco y sin traza: simplemente no
// existiría en pantalla. Así que se recorre la UNIÓN — el esqueleto primero, y detrás los que
// lleguen, en el orden en que lleguen.
/**
 * Los hitos a pintar, EN ORDEN, como una LISTA DE ENTRADAS y no como un mapa por clase.
 *
 * ⚠️⚠️ S419 — LA CLASE DEJA DE SER ÚNICA. Una orden puede replanificarse N veces, así que llegan N
 * hitos `REPLANNED`. Esto se resolvía indexando `{[kind]: milestone}`, y ese mapa **se habría
 * quedado con la última en silencio**: sin error, sin hueco y sin traza, con la línea de tiempo
 * aparentemente entera. Es la pérdida invisible de siempre, solo que al revés que en S417 — allí
 * sobraba un hito, aquí faltarían.
 *
 * ⚠️⚠️ EL ORDEN ES DE SUN Y NO SE RECALCULA AQUÍ. Sus dos reglas están escritas y probadas allí:
 * solo ordenan las anclas con fecha de algo que OCURRIÓ (`PLANNED` lleva una previsión, así que no
 * ordena), y lo ya ocurrido nunca se dibuja detrás de lo que aún no ha ocurrido. Reordenar en
 * cliente sería una segunda definición del mismo criterio — y ya sabemos cuál de las dos diverge.
 * 📌 Mi propia regla —«las replanificaciones entre STARTED y CLOSED»— coincidía en el caso normal
 * y fallaba en el de una replanificación ANTERIOR al arranque, que Sun ya tiene probado. Se
 * respeta el orden servido.
 *
 * Lo único que este lado garantiza es RN60: las cuatro anclas salen SIEMPRE, las traiga el DTO o
 * no. Un ancla que no venga se inserta en su hueco del esqueleto, delante de la primera ancla
 * posterior que sí esté.
 *
 * @returns {Array<{key: string, kind: string, milestone: object}>}
 */
export const milestoneEntriesOf = (milestones) => {
  const entries = (milestones || [])
    .filter((milestone) => !!milestone && !!milestone.kind)
    // ⚠ La clave sale del `id` que sirve Sun —un identificador estable de la transición, nulo en
    // las anclas— y NUNCA de la posición: insertar una replanificación anterior desplaza todas las
    // de detrás, y una clave por posición haría que React reutilizara el nodo equivocado.
    .map((milestone) => ({ key: milestone.id || milestone.kind, kind: milestone.kind, milestone }))

  MILESTONE_ORDER.forEach((kind, skeletonIndex) => {
    if (entries.some((entry) => entry.kind === kind)) return
    const nextAnchor = MILESTONE_ORDER.slice(skeletonIndex + 1)
      .find((later) => entries.some((entry) => entry.kind === later))
    const at = nextAnchor ? entries.findIndex((entry) => entry.kind === nextAnchor) : entries.length
    entries.splice(at, 0, { key: kind, kind, milestone: {} })
  })
  return entries
}

// `Order created` y no `Created`: la clave inglesa a secas la ocupa la biblioteca de BOMs como
// cabecera de columna de fecha, con traducciones que no encajan en una serie de hitos (en euskera,
// el sustantivo "fecha de creación" entre tres participios). El texto en pantalla no cambia.
const MILESTONE_LABEL_KEYS = {
  CREATED: 'Order created',
  // `Planned` se REUTILIZA, al revés que `Created`. Comprobadas las ocho: Planificado ·
  // Planifikatua · Plānots · Planificat · Geplant · Planifié · Planeado — todas participio o
  // adjetivo, que es la forma de la serie (Creada · Planificada · En curso · Terminada). Su único
  // otro uso es la cabecera de un acordeón en MOS, con ese mismo sentido, así que atarlas no
  // arrastra a nadie a un texto que no le sirva.
  PLANNED: 'Planned',
  // S419. En castellano «Replanificada», en femenino: concuerda con la serie (Creada · Planificada
  // · En curso · Terminada), que habla de la ORDEN.
  REPLANNED: 'Replanned',
  // ⚠️ LAUNCHED ya no está en el esqueleto pero conserva su rótulo A PROPÓSITO: un backend
  // anterior a S417 todavía lo sirve, y sin esta entrada saldría —por el respaldo, al final— con
  // el enum en crudo y en mayúsculas. Que un jar viejo se lea mal es peor que dos líneas de más.
  LAUNCHED: 'Launched',
  STARTED: 'In progress',
  CLOSED: 'Finished'
}

// S420 — la frase del motivo se arma AQUÍ, a partir del código que manda Sun.
//
// ⚠️⚠️ ANTES LLEGABA LA FRASE HECHA, EN CASTELLANO, y se pintaba igual en los ocho idiomas: un
// texto compuesto en el servidor no pasa por los ficheros de traducción. Ahora Sun manda un código
// y sus argumentos, y el idioma vuelve al único sitio donde existe.
//
// ⚠️ SON CUATRO CÓDIGOS Y NO TRES CON UN ARGUMENTO OPCIONAL, y el javadoc de Sun explica por qué:
// `UNITS_LEFT` y `UNITS_LEFT_UNKNOWN` separados evitan que esta pantalla tenga que RAMIFICAR SOBRE
// UN ARGUMENTO AUSENTE para decidir qué frase enseñar — una ausencia que habría que inferir en vez
// de leer, que es el defecto que este cambio quita.
//
// 📌 Un código que esta pantalla no conozca NO se pinta en crudo ni se calla: cae al literal
// genérico, igual que un hito desconocido sale al final en vez de desaparecer.
const PENDING_REASON_KEYS = {
  NOT_IN_CURRENT_PLAN: 'The order is not in the current plan',
  NO_OPERATION_STARTED: 'No operation has started yet',
  UNITS_LEFT: '{{units}} units left to make',
  UNITS_LEFT_UNKNOWN: 'There are units left to make'
}

/**
 * @param {object} pendingReason `{ code, args, legacyText }` tal como lo deja la frontera
 * @param {function} t_ el traductor de quien pinta
 */
const pendingReasonText = (pendingReason, t_) => {
  const reason = pendingReason || {}
  // ⚠️ El respaldo de un jar anterior a S420: la frase ya viene en un idioma decidido y lo único
  // que se puede hacer con ella es enseñarla tal cual. Se retira cuando el backend se reconstruya.
  if (reason.legacyText) return reason.legacyText
  const key = PENDING_REASON_KEYS[reason.code]
  if (!key) return t_('Not reached yet')
  return t_(key, reason.args || {})
}

// Story 178 (S75/S76, DP16): the CLOSED milestone of an order closed with incomplete parts is labelled
// "Partially finished". Jupiter deduces it from the HEADER's `statusProgress` the dialog already holds —
// N10 (`/detail/progress`) gains no field for it, and every other milestone keeps its label.
const milestoneLabelKey = (kind, statusProgress) => {
  // Story 204 (RN1.5): the key is the code itself.
  if (kind === 'CLOSED' && statusProgress === PARTIALLY_FINISHED) return PARTIALLY_FINISHED
  return MILESTONE_LABEL_KEYS[kind] || ''
}

// The origin of a milestone is a code of the server: it is shown translated; a code this screen
// does not know is shown as it comes (never hidden).
const MILESTONE_ORIGIN_KEYS = {
  DETAIL: 'From the order detail',
  ORDER_LIST: 'From the order list'
}

// The line of the order's milestones: where the order has got to (the last milestone reached).
const MilestonesTimeline = ({ milestones, statusProgress }) => {
  const { t_ } = useSmartTranslation()
  const theme = useTheme()
  const { formatDateTime } = useDateFormatter()
  const milestoneEntries = milestoneEntriesOf(milestones)
  // HASTA DÓNDE HA LLEGADO LA ORDEN: el último hito alcanzado, que es lo que una línea de tiempo
  // viene a responder y cuatro columnas sueltas no respondían. Se busca el ÚLTIMO y no el primero
  // sin alcanzar: un hueco en medio (una OF anterior al histórico, D16) no debe mover el frente
  // hacia atrás.
  const frontierIndex = milestoneEntries.reduce(
    (last, entry, index) => (entry.milestone.state === 'REACHED' ? index : last), -1)

  /* ═══ LOS CUATRO HITOS PASAN DE CUATRO COLUMNAS SUELTAS A UNA LÍNEA DE TIEMPO (nota n2) ═══
    ⚠️⚠️ EL PROBLEMA NO ERA LA MAQUETA, ERA QUE TRES DE LOS CUATRO DICEN «Not recorded».
    Cuatro rótulos en versalitas, tres de ellos seguidos de la misma frase gris, se leen como
    una pantalla que no ha cargado — y la reacción no es «esta orden no llegó a lanzarse»,
    es «esto está roto» o «falta el dato». La línea lo separa por construcción, y de tres
    maneras a la vez, ninguna de ellas el color:
      · el PUNTO dice si el hito se alcanzó (relleno) o no (hueco), que es un hecho sobre la
        ORDEN y no sobre la carga;
      · el TRAMO recorrido llega hasta el último alcanzado y el resto queda por delante: los
        hitos sin fecha están donde tiene que estar lo que aún no ha pasado;
      · y cada hueco DICE LO SUYO (§2.2, RN8) — la raya larga donde iría la fecha, y debajo o
        el motivo que da el servidor («quedan 14 unidades por fabricar») o «sin registro».
    📌 «Sin registro» y «aún no alcanzado» NO se funden en una sola frase, aunque el lienzo
    pinte las tres columnas iguales. Son dos cosas distintas: el pendiente va a ocurrir, y el
    UNRECORDED es una OF anterior al histórico de transiciones (D16, sin backfill) — pudo
    ocurrir y nadie lo anotó. Fundirlas ahorraría una clave y perdería la única pista de por
    qué esa fecha no está. */
  return (
    <Box
      data-testid='milestones'
      sx={{ mb: 3, p: 2.5, borderRadius: 2, border: `1px solid ${alpha(theme.palette.text.primary, 0.08)}`, backgroundColor: raisedColor(theme) }}
    >
      <Typography variant='caption' sx={{ display: 'block', fontWeight: 800, letterSpacing: 1, textTransform: 'uppercase', color: 'text.secondary', mb: 1.5 }}>
        {t_('Order milestones')}
      </Typography>
      <Stack direction='row' data-testid='milestones-timeline' alignItems='flex-start' sx={{ flexWrap: 'nowrap', overflowX: 'auto' }}>
        {milestoneEntries.map(({ key, kind, milestone }, index) => {
          const reached = milestone.state === 'REACHED'
          const current = index === frontierIndex
          const last = index === milestoneEntries.length - 1
          // ⚠️ Un identificador REPETIDO en el DOM rompe `getByTestId`, y con S419 la clase se
          // repite. Las anclas conservan el suyo tal cual —`milestone-PLANNED`, para no mover los
          // tests que ya existen— y los bloques que traen `id` lo llevan detrás.
          const testId = milestone.id ? `milestone-${kind}-${milestone.id}` : `milestone-${kind}`
          return (
            <Box
              key={key}
              data-testid={testId}
              data-state={milestone.state || 'UNRECORDED'}
              data-current={current ? 'true' : 'false'}
              sx={{ flex: last ? '0 0 210px' : '1 1 0', minWidth: 140 }}
            >
              <Stack direction='row' alignItems='center' sx={{ mb: 1 }}>
                <Box
                  data-testid={testId.replace('milestone-', 'milestone-dot-')}
                  data-reached={reached ? 'true' : 'false'}
                  sx={{
                    width: current ? 13 : 11,
                    height: current ? 13 : 11,
                    borderRadius: '50%',
                    flexShrink: 0,
                    backgroundColor: reached ? (current ? theme.palette.info.main : theme.palette.success.main) : 'transparent',
                    border: reached ? 'none' : `1px solid ${alpha(theme.palette.text.primary, 0.25)}`
                  }}
                />
                {!last && (
                  <Box
                    data-testid={testId.replace('milestone-', 'milestone-connector-')}
                    data-travelled={index < frontierIndex ? 'true' : 'false'}
                    sx={{ flexGrow: 1, height: 2, backgroundColor: index < frontierIndex ? alpha(theme.palette.success.main, 0.5) : alpha(theme.palette.text.primary, 0.12) }}
                  />
                )}
              </Stack>
              {/* The gap between milestones lives on the text, not on the box: padding on the box would cut the connector short of the next dot. */}
              <Box sx={{ pr: last ? 0 : 2 }}>
                {/* Un hito que el servidor no nombra se pinta con su propio `kind`: es feo a
                propósito. Inventarle un rótulo bonito escondería que esta pantalla no lo
                conoce, y callarlo lo borraría de la línea. */}
                <Typography
                  component='span'
                  variant='caption'
                  sx={{ display: 'block', fontWeight: current ? 800 : 600, color: reached ? (current ? 'info.main' : 'text.primary') : 'text.secondary' }}
                >
                  {milestoneLabelKey(kind, statusProgress) ? t_(milestoneLabelKey(kind, statusProgress)) : kind}
                </Typography>
                {reached
                  ? (
                    <>
                      <Typography data-testid='milestone-date' component='span' variant='body2' sx={{ display: 'block', fontWeight: 700, fontFamily: 'monospace' }}>{formatDateTime(milestone.occurredAt)}</Typography>
                      <Typography data-testid='milestone-origin' component='span' variant='caption' sx={{ display: 'block', color: 'text.secondary' }}>{MILESTONE_ORIGIN_KEYS[milestone.origin] ? t_(MILESTONE_ORIGIN_KEYS[milestone.origin]) : (milestone.origin || EMPTY_DASH)}</Typography>
                    </>
                    )
                  : (
                // §2.2 — el hueco de la fecha NO se deja en blanco: la raya larga ocupa su
                // sitio, con su misma monoespaciada, para que la columna siga alineada y para
                // que se vea que ahí no hay fecha, no que la fecha se perdió.
                    <Typography data-testid='milestone-no-date' component='span' variant='body2' sx={{ display: 'block', fontWeight: 700, fontFamily: 'monospace', color: 'text.secondary' }}>{EMPTY_DASH}</Typography>
                    )}
                {/* ⚠️⚠️ S419 — LAS DOS FECHAS DE UN BLOQUE DE REPLANIFICACIÓN NO SON LA MISMA, y se
                parecen demasiado: las dos llegan como cadena ISO de idéntica forma. `occurredAt`
                es CUÁNDO SE REPLANIFICÓ —es lo que coloca el bloque en la línea, y ya está
                pintado arriba— y `plannedStartAt` es LA FECHA A LA QUE SE MOVIÓ EL TRABAJO, que
                normalmente está en el FUTURO. Pintar la segunda como si fuera la posición
                dibujaría historia por delante de hoy. Por eso va debajo y con su propio rótulo,
                nunca en el hueco de la fecha del hito. */}
                {!!milestone.plannedStartAt && (
                  <Typography data-testid='milestone-planned-start' component='span' variant='caption' sx={{ display: 'block', color: 'text.secondary' }}>
                    {`${t_('New planned start')}: ${formatDateTime(milestone.plannedStartAt)}`}
                  </Typography>
                )}
                {milestone.state === 'PENDING' && (
                  <Typography data-testid='milestone-pending-reason' component='span' variant='caption' sx={{ display: 'block', color: 'text.secondary' }}>
                    {/* El motivo lo redacta Sun; si no viene, la ausencia se dice igual — un hito
                    pendiente sin frase seguiría siendo un hito que no ha ocurrido. */}
                    {pendingReasonText(milestone.pendingReason, t_)}
                  </Typography>
                )}
                {!reached && milestone.state !== 'PENDING' && (
                  <Typography data-testid='milestone-unrecorded' component='span' variant='caption' sx={{ display: 'block', color: 'text.secondary' }}>
                    {/* Cubre además el hito que el DTO NO trae: RN60 lo pinta igual, y de un hito
                    del que no llega nada lo único cierto es que no hay registro. */}
                    {t_('Not recorded')}
                  </Typography>
                )}
              </Box>
            </Box>
          )
        })}
      </Stack>
    </Box>
  )
}

export default MilestonesTimeline
